import { type VariantProps, cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";
import { cn } from "@/lib/utils";

/** KORI `.badge`: the status trio (tint ground, ink text, mark dot) in a pill. */
const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-pill border py-1 font-sans font-semibold leading-[1.4] [&_svg]:size-3",
  {
    variants: {
      variant: {
        neutral: "border-transparent bg-surface-soft text-heading [--badge-dot:var(--text-heading)]",
        success: "border-transparent bg-success-tint text-success-ink [--badge-dot:var(--success)]",
        warning: "border-transparent bg-warning-tint text-warning-ink [--badge-dot:var(--warning)]",
        danger: "border-transparent bg-danger-tint text-danger-ink [--badge-dot:var(--danger)]",
        caution: "border-transparent bg-caution-tint text-caution-ink [--badge-dot:var(--caution)]",
        info: "border-transparent bg-info-tint text-info-ink [--badge-dot:var(--info)]",
        outline: "border-border-strong bg-transparent text-muted [--badge-dot:var(--text-muted)]",
        /** EstateLark: a placeholder value, dashed so it never reads as a real one. */
        sample: "border-dashed border-border-strong bg-transparent text-muted [--badge-dot:var(--text-muted)]",
      },
      size: {
        sm: "px-2 text-[0.6875rem]",
        default: "px-3 text-xs",
        lg: "px-4 text-sm",
      },
    },
    defaultVariants: { variant: "neutral", size: "default" },
  },
);

type BadgeProps = React.ComponentPropsWithoutRef<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; dot?: boolean };

/** forwardRef so a tooltip can anchor to the badge on React 18. */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, variant, size, asChild = false, dot = false, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : "span";
  return (
    <Comp ref={ref} data-slot="badge" className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot ? <span aria-hidden className="size-[0.4rem] flex-none rounded-full bg-(--badge-dot)" /> : null}
      {children}
    </Comp>
  );
});

export { Badge, badgeVariants };
